import { Trans, t } from '@lingui/macro'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Paper, Typography } from '@mui/material'
import { AnalyticsAdminTasksService, OpenAPI, TaskChainDTO, falconApiConfig } from '@om1/falcon-api'
import { notificationActions } from '@om1/platform-notifications'
import React, { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

const getRefreshOptions = () => [
    {
        id: 2,
        name: t`Full Analytics Layer`
    },
    {
        id: 1,
        name: t`Product Custom Cohort`
    }
]

export const AnalyticsBuildPageComponent: React.FC = () => {
    const dispatch = useDispatch()
    const [isRefreshing, setIsRefreshing] = React.useState<Record<number, boolean>>({})
    const [taskId, setTaskId] = React.useState<string | null>(null)
    const [completionTime, setCompletionTime] = React.useState<string | null>(null)
    const [taskIdType, setTaskIdType] = React.useState<number | null>(null)
    const [dialogOpen, setDialogOpen] = React.useState(false)
    const [selectedId, setSelectedId] = React.useState<number | null>(null)
    const wsRef = useRef<WebSocket | null>(null)

    const refreshOptions = getRefreshOptions()

    const handleRefreshClick = (id: number) => {
        setSelectedId(id)
        setDialogOpen(true)
    }

    const handleConfirm = () => {
        setDialogOpen(false)
        if (selectedId !== null) {
            handleRefresh(selectedId)
        }
    }

    const handleCancel = () => {
        setDialogOpen(false)
        setSelectedId(null)
    }

    const handleRefresh = useCallback(
        async (id: number) => {
            try {
                setIsRefreshing((prev) => ({ ...prev, [id]: true }))
                setTaskIdType(id)
                setTaskId(null)
                setCompletionTime(null)
                let taskChain: TaskChainDTO
                if (id === 1) {
                    taskChain = await AnalyticsAdminTasksService.refreshProductCustomCohortAnalyticsAdminTasksRefreshProductCustomCohortGet()
                } else {
                    taskChain = await AnalyticsAdminTasksService.analyticsDagAnalyticsAdminTasksDagGet({})
                }

                setTaskId(taskChain.taskId)
                const websocketUrl = `${falconApiConfig.falconApiUrl?.replace(new RegExp(/^http/), 'ws')}/tasks/ws/${taskChain.taskId}`
                const ws = new WebSocket(websocketUrl)
                wsRef.current = ws

                ws.onopen = () => {
                    ws.send(JSON.stringify({ token: String(OpenAPI.TOKEN) }))
                }

                ws.onmessage = (event) => {
                    const message = JSON.parse(event.data)

                    if (message.status?.toLowerCase() === 'success') {
                        dispatch(notificationActions.success(t`Refresh completed successfully`))
                        setIsRefreshing((prev) => ({ ...prev, [id]: false }))
                        setCompletionTime(new Date().toLocaleString())
                    } else if (message.status?.toLowerCase() === 'failure') {
                        dispatch(notificationActions.error(message.result?.error || t`Refresh failed`))
                        setIsRefreshing((prev) => ({ ...prev, [id]: false }))
                    }
                }

                ws.onclose = () => {
                    setIsRefreshing((prev) => ({ ...prev, [id]: false }))
                }

                ws.onerror = (error) => {
                    dispatch(notificationActions.error(t`WebSocket connection error`))
                    setIsRefreshing((prev) => ({ ...prev, [id]: false }))
                }
            } catch (error) {
                dispatch(notificationActions.error(t`Failed to start refresh`))
                setIsRefreshing((prev) => ({ ...prev, [id]: false }))
            }
        },
        [dispatch]
    )

    useEffect(() => {
        return () => {
            if (wsRef.current) {
                wsRef.current.close()
            }
        }
    }, [])

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant='h4' component='h1' gutterBottom>
                    <Trans>Analytics Layer Refresh</Trans>
                </Typography>
                <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
                    <Trans>
                        Note: If you refresh the page or navigate away, you will lose tracking on the running task(s). Refresh processes may take
                        anywhere from 20 to 60 minutes to complete.
                    </Trans>
                </Typography>
                {refreshOptions.map((ele, index) => {
                    return (
                        <React.Fragment key={ele.id}>
                            <Box sx={{ mb: 4 }}>
                                <Typography variant='h5' component='h2' gutterBottom>
                                    {ele.name}
                                </Typography>

                                <Button
                                    variant='contained'
                                    color='primary'
                                    startIcon={isRefreshing[ele.id] ? <CircularProgress size={20} color='inherit' /> : <RefreshIcon />}
                                    onClick={() => handleRefreshClick(ele.id)}
                                    disabled={Object.values(isRefreshing).some((value) => value)}
                                    sx={{
                                        minWidth: 100,
                                        position: 'relative'
                                    }}
                                >
                                    {isRefreshing[ele.id] ? <Trans>Refreshing...</Trans> : <Trans>Refresh</Trans>}
                                </Button>
                            </Box>
                            {taskId && taskIdType === ele.id && (
                                <Typography variant='body2' sx={{ mt: 2, color: 'text.secondary' }}>
                                    <Trans>Task ID:</Trans> {taskId}
                                </Typography>
                            )}
                            {taskId && taskIdType === ele.id && completionTime && (
                                <Typography variant='body2' sx={{ mt: 1, color: 'success.main' }}>
                                    <Trans>Completed at:</Trans> {completionTime}
                                </Typography>
                            )}
                            {index < refreshOptions.length - 1 && <Divider sx={{ my: 3 }} />}
                        </React.Fragment>
                    )
                })}
            </Paper>

            <Dialog open={dialogOpen} onClose={handleCancel}>
                <DialogTitle>
                    <Trans>Confirm Refresh</Trans>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        <Trans>
                            This refresh operation may take up to 60 minutes to complete and consume significant computational resources. Are you sure
                            you want to proceed?
                        </Trans>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} color='primary'>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button onClick={handleConfirm} color='primary' variant='contained'>
                        <Trans>Continue</Trans>
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
